import {
  AlertTriangle,
  Ban,
  Hammer,
  Mail,
  NotebookTabs,
  Settings2,
  X,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import Accordian from "../accordian";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import useAuthStore from "../../store/Auth";
import { useTranslation } from "react-i18next";
import { APP_ROUTES, ROUTE_NAMES } from "../../routes";
import { Calendar, Home, Users } from "lucide-react";
import { RolePrivilege } from "../../store/Auth/interface";
import { USER_ROLES_IDS } from "../../constants/common";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SidebarItem {
  id: string;
  title: string;
  icon?: React.ReactNode;
  url?: string;
  routeName?: ROUTE_NAMES;
  items?: {
    id: number;
    title: string;
    url: string;
    subUrls?: string[];
    routeName: ROUTE_NAMES;
  }[];
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("");

  const navigate = useNavigate();

  const location = useLocation();

  const { user, userRoles } = useAuthStore();

  const { i18n, t } = useTranslation();

  const isArabic = i18n.language === "ar";

  const sidebarItems = [
    {
      id: "1",
      title: t("home"),
      icon: <Home className="w-5" />,
      url: APP_ROUTES.HOME,
      routeName: ROUTE_NAMES.HOME_SEARCH,
    },
    {
      id: "2",
      title: t("visitRequest"),
      items: [
        {
          id: 1,
          title: t("approveVisitRequest"),
          url: APP_ROUTES.APPROVE_VISIT_REQUEST,
          subUrls: [APP_ROUTES.APPROVE_REQUEST],
          routeName: ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH,
        },
        {
          id: 2,
          title: t("createVisitRequest"),
          url: APP_ROUTES.CREATE_VISIT_REQUEST,
          subUrls: [
            APP_ROUTES.INDIVIDUAL_VISIT_REQUEST,
            APP_ROUTES.GROUP_VISIT_REQUEST,
            APP_ROUTES.VIP_VISIT_REQUEST,
          ],
          routeName: ROUTE_NAMES.UPDATE_REQUEST_CREATE,
        },
        {
          id: 3,
          title: t("searchVisitRequest"),
          url: APP_ROUTES.SEARCH_VISIT_REQUEST,
          routeName: ROUTE_NAMES.UPDATE_REQUEST_SEARCH,
        },
      ],
      icon: <Calendar className="w-5" />,
    },
    {
      id: "9",
      title: t("permitRequest"),
      items: [
        ...(user?.n_RoleID !== USER_ROLES_IDS.ADMIN
          ? [
            {
              id: 1,
              title: t("searchPermitRequest"),
              url: APP_ROUTES.SEARCH_PERMIT_REQUEST,
              subUrls: [APP_ROUTES.SEARCH_PERMIT_REQUEST],
              routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
            },
          ]
          : []),
        {
          id: 2,
          title: t("approvePermitRequest"),
          url: APP_ROUTES.APPROVE_PERMIT_REQUEST,
          subUrls: [APP_ROUTES.APPROVE_PERMIT_REQUEST],
          routeName: ROUTE_NAMES.APPROVE_PERMIT_REQUEST_SEARCH,
        },
        {
          id: 3,
          title: t("permitToWork"),
          url: APP_ROUTES.PERMIT_TO_WORK,
          subUrls: [APP_ROUTES.PERMIT_TO_WORK],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 6,
          title: t("lostVehicleStickerIdCard"),
          url: APP_ROUTES.LOST_VEHICLE_STICKER_ID_CARD,
          subUrls: [APP_ROUTES.LOST_VEHICLE_STICKER_ID_CARD],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 7,
          title: t("parkingStickerRequest"),
          url: APP_ROUTES.PARKING_STICKER_REQUEST,
          subUrls: [APP_ROUTES.PARKING_STICKER_REQUEST],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 8,
          title: t("officeAccessAuthorization"),
          url: APP_ROUTES.OFFICE_ACCESS_AUTHORIZATION,
          subUrls: [APP_ROUTES.OFFICE_ACCESS_AUTHORIZATION],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 9,
          title: t("issuanceRenewalIdForStaffFromContractedCompanies"),
          url: APP_ROUTES.ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES,
          subUrls: [APP_ROUTES.ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 4,
          title: t("warehouseInOutPermit"),
          url: APP_ROUTES.WAREHOUSE_IN_OUT_PERMIT,
          subUrls: [APP_ROUTES.WAREHOUSE_IN_OUT_PERMIT],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
        {
          id: 5,
          title: t("pcInOutPermit"),
          url: APP_ROUTES.PC_IN_OUT_PERMIT,
          subUrls: [APP_ROUTES.PC_IN_OUT_PERMIT],
          routeName: ROUTE_NAMES.PERMIT_REQUEST_SEARCH,
        },
      ],
      icon: <Hammer className="w-5" />,
    },
    {
      id: "3",
      title: t("users"),
      items: [
        {
          id: 1,
          title: t("addUsers"),
          url: APP_ROUTES.ADD_USERS,
          routeName: ROUTE_NAMES.USERS_CREATE,
        },
        {
          id: 2,
          title: t("searchUsers"),
          url: APP_ROUTES.SEARCH_USERS,
          routeName: ROUTE_NAMES.USERS_SEARCH,
        },
        {
          id: 3,
          title: t("manageRoles"),
          url: APP_ROUTES.MANAGE_ROLES,
          routeName: ROUTE_NAMES.ROLE_PRIVILEGES_SEARCH,
        },
      ],
      icon: <Users className="w-5" />,
    },
    {
      id: "5",
      title: t("reports"),
      items: [
        {
          id: 1,
          title: t("visitRequestReport"),
          url: APP_ROUTES.VISIT_REQUEST_REPORT,
          routeName: ROUTE_NAMES.VISIT_REQUEST_REPORT_SEARCH,
        },
        {
          id: 2,
          title: t("visitorsReport"),
          url: APP_ROUTES.VISITORS_REPORT,
          routeName: ROUTE_NAMES.VISITORS_REPORT_SEARCH,
        },
        {
          id: 5,
          title: t("accessLogReport"),
          url: APP_ROUTES.ACCESS_LOG_REPORT,
          routeName: ROUTE_NAMES.ACCESS_LOG_REPORT_SEARCH,
        },
        {
          id: 3,
          title: t("externalUsersReport"),
          url: APP_ROUTES.EXTERNAL_USERS_REPORT,
          routeName: ROUTE_NAMES.EXTERNAL_USERS_REPORT_SEARCH,
        },
        {
          id: 4,
          title: t("loginUserReport"),
          url: APP_ROUTES.LOGIN_USER_REPORT,
          routeName: ROUTE_NAMES.LOGIN_USER_REPORT_SEARCH,
        },
        {
          id: 7,
          title: t("auditLogsReport"),
          url: APP_ROUTES.AUDIT_LOGS_REPORT,
          routeName: ROUTE_NAMES.AUDIT_LOGS_REPORT_SEARCH,
        },
        {
          id: 8,
          title: t("overstayedVisitorsReport"),
          url: APP_ROUTES.OVERSTAYED_VISITORS_REPORT,
          routeName: ROUTE_NAMES.OVERSTAYED_VISITORS_REPORT_SEARCH,
        },
        {
          id: 10,
          title: t("blacklistingReport"),
          url: APP_ROUTES.BLACKLISTING_REPORT,
          routeName: ROUTE_NAMES.BLACKLISTING_REPORT_SEARCH,
        },
        {
          id: 8,
          title: t("frErrorsReport"),
          url: APP_ROUTES.FR_ERRORS_REPORT,
          routeName: ROUTE_NAMES.FR_ERRORS_REPORT_SEARCH,
        },
        {
          id: 9,
          title: t("ratingsReport"),
          url: APP_ROUTES.RATING_REPORT,
          routeName: ROUTE_NAMES.RATING_REPORT_SEARCH,
        },
      ],
      icon: <NotebookTabs className="w-5" />,
    },
    {
      id: "10",
      title: t("emerygencyEvacuation"),
      items: [
        {
          id: 1,
          title: t("liveEvacuationDashboard"),
          url: APP_ROUTES.LIVE_EVACUATION_DASHBOARD,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH,
        },
        {
          id: 2,
          title: t("startEvacuation"),
          url: APP_ROUTES.START_EVACUATION,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_CREATE,
        },
        {
          id: 3,
          title: t("musterStation"),
          url: APP_ROUTES.MUSTER_STATION,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH,
        },
        {
          id: 4,
          title: t("evacuationEvents"),
          url: APP_ROUTES.EVACUATION_EVENTS,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH,
        },
        {
          id: 5,
          title: t("evacuationReport"),
          url: APP_ROUTES.EVACUATION_REPORT,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_REPORT_SEARCH,
        },
      ],
      icon: <AlertTriangle className="w-5" />,
    },
    {
      id: "6",
      title: t("configuration"),
      items: [
        {
          id: 1,
          title: t("systemCodes"),
          url: APP_ROUTES.SYSTEM_CODES,
          routeName: ROUTE_NAMES.SYSTEM_CODES_SEARCH,
        },
        {
          id: 9,
          title: t("activeDirectory"),
          url: APP_ROUTES.ACTIVE_DIRECTORY_CONFIG,
          routeName: ROUTE_NAMES.ACTIVE_DIRECTORY_CONFIG_SEARCH,
        },
        {
          id: 3,
          title: t("emailConfig"),
          url: APP_ROUTES.EMAIL_CONFIG,
          routeName: ROUTE_NAMES.EMAIL_CONFIG_SEARCH,
        },
        {
          id: 18,
          title: t("smsConfig"),
          url: APP_ROUTES.SMS_CONFIG,
          routeName: ROUTE_NAMES.SMS_CONFIG_SEARCH,
        },
        {
          id: 4,
          title: t("locations"),
          url: APP_ROUTES.LOCATIONS,
          routeName: ROUTE_NAMES.LOCATIONS_SEARCH,
        },
        {
          id: 11,
          title: t("buildings"),
          url: APP_ROUTES.BUILDINGS_CONFIGURATION,
          routeName: ROUTE_NAMES.BUILDINGS_SEARCH_CONFIGURATION,
        },
        {
          id: 6,
          title: t("departments"),
          url: APP_ROUTES.DEPARTMENTS,
          routeName: ROUTE_NAMES.DEPARTMENTS_SEARCH,
        },
        {
          id: 5,
          title: t("gates"),
          url: APP_ROUTES.GATES_CONFIG,
          routeName: ROUTE_NAMES.GATES_SEARCH_CONFIG,
        },

        {
          id: 7,
          title: t("meetingRooms"),
          url: APP_ROUTES.MEETING_ROOMS_CONFIG,
          routeName: ROUTE_NAMES.MEETING_ROOMS_SEARCH_ONFIG,
        },
        {
          id: 2,
          title: t("visitPurpose"),
          url: APP_ROUTES.VISIT_PURPOSE,
          routeName: ROUTE_NAMES.VISIT_PURPOSE_SEARCH,
        },
        {
          id: 8,
          title: t("accessGroups"),
          url: APP_ROUTES.ACCESS_GROUPS,
          routeName: ROUTE_NAMES.ACCESS_GROUPS_SEARCH,
        },
        {
          id: 15,
          title: t("devices"),
          url: APP_ROUTES.DEVICES_CONFIG,
          routeName: ROUTE_NAMES.FR_DEVICES_CONFIG_SEARCH,
        },
        {
          id: 16,
          title: t("facialRecognition"),
          url: APP_ROUTES.FR_CONFIG,
          routeName: ROUTE_NAMES.FR_CONFIG_SEARCH,
        },
        {
          id: 17,
          title: t("musterPoints"),
          url: APP_ROUTES.MUSTER_POINTS_CONFIG,
          routeName: ROUTE_NAMES.MUSTER_POINTS_CREATE,
        },
        {
          id: 14,
          title: t("criticalNationalities"),
          url: APP_ROUTES.CRITICAL_NATIONALITIES_CONFIG,
          routeName: ROUTE_NAMES.CRITICAL_NATIONALITIES_CONFIG_SEARCH,
        },
        {
          id: 12,
          title: t("externalHelp"),
          url: APP_ROUTES.EXTERNAL_HELP_CONFIG,
          routeName: ROUTE_NAMES.EXTERNAL_HELP_CONFIG_SEARCH,
        },
        {
          id: 10,
          title: t("dynamicWorkflow"),
          url: APP_ROUTES.DYNAMIC_WORKFLOW_CONFIG,
          routeName: ROUTE_NAMES.DYNAMIC_WORKFLOW_CONFIG_SEARCH,
        },
      ],
      icon: <Settings2 className="w-5" />,
    },
    {
      id: "7",
      title: t("emailTemplates"),
      items: [
        {
          id: 1,
          title: t("visitSubmittedTemplate"),
          url: APP_ROUTES.VISIT_SUBMITTED,
          routeName: ROUTE_NAMES.VISIT_SUBMITTED_SEARCH,
        },
        {
          id: 2,
          title: t("visitRequestScheduleTemplate"),
          url: APP_ROUTES.VISIT_REQUEST_SCHEDULE_TEMPLATE,
          routeName: ROUTE_NAMES.VISIT_REQUEST_SCHEDULE_TEMPLATE_SEARCH,
        },
        {
          id: 3,
          title: t("visitApprovalEmailTemplate"),
          url: APP_ROUTES.VISIT_APPROVED_HOST_EMAIL,
          routeName: ROUTE_NAMES.VISIT_APPROVED_HOST_EMAIL_SEARCH,
        },
        {
          id: 4,
          title: t("visitRejectedHostEmailTemplate"),
          url: APP_ROUTES.VISIT_REJECTED_HOST_EMAIL,
          routeName: ROUTE_NAMES.VISIT_REJECTED_HOST_EMAIL_SEARCH,
        },
        {
          id: 5,
          title: t("visitScheduleToHostTemplate"),
          url: APP_ROUTES.EMAIL_TO_HOST_TEMPLATE,
          routeName: ROUTE_NAMES.EMAIL_TO_HOST_TEMPLATE_SEARCH,
        },
        {
          id: 6,
          title: t("visitRequestRejectedTemplate"),
          url: APP_ROUTES.VISIT_REQUEST_REJECTED_TEMPLATE,
          routeName: ROUTE_NAMES.VISIT_REQUEST_REJECTED_TEMPLATE_SEARCH,
        },
        {
          id: 17,
          title: t("emergencyEvacuationTemplate"),
          url: APP_ROUTES.EMERGENCY_EVACUATION_TEMPLATE,
          routeName: ROUTE_NAMES.EMERGENCY_EVACUATION_TEMPLATE_SEARCH,
        },
        {
          id: 7,
          title: t("externalPasswordResetTemplate"),
          url: APP_ROUTES.EXTERNAL_PASSWORD_RESET,
          routeName: ROUTE_NAMES.EXTERNAL_PASSWORD_RESET_SEARCH,
        },
        {
          id: 15,
          title: t("OTPTemplate"),
          url: APP_ROUTES.OTP_TEMPLATE,
          routeName: ROUTE_NAMES.OTP_TEMPLATE_SEARCH,
        },
        {
          id: 8,
          title: t("externalVisitRequestCancelToHostTemplate"),
          url: APP_ROUTES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_HOST,
          routeName: ROUTE_NAMES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_HOST_SEARCH,
        },
        {
          id: 9,
          title: t("externalVisitRequestCancelToVisitorTemplate"),
          url: APP_ROUTES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR,
          routeName:
            ROUTE_NAMES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR_SEARCH,
        },
        {
          id: 15,
          title: t("internalVisitRequestCancelToHostTemplate"),
          url: APP_ROUTES.INTERNAL_CANCEL_VISIT_REQUEST_TO_HOST,
          routeName: ROUTE_NAMES.INTERNAL_CANCEL_VISIT_REQUEST_TO_HOST_SEARCH,
        },
        {
          id: 16,
          title: t("internalVisitRequestCancelToVisitorTemplate"),
          url: APP_ROUTES.INTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR,
          routeName:
            ROUTE_NAMES.INTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR_SEARCH,
        },
        {
          id: 10,
          title: t("externalAccountRegistrationTemplate"),
          url: APP_ROUTES.EXTERNAL_ACCOUNT_REGISTRATION_TEMPLATE,
          routeName: ROUTE_NAMES.EXTERNAL_ACCOUNT_REGISTRATION_TEMPLATE_SEARCH,
        },
        {
          id: 11,
          title: t("visitRequestedForInformationToHostTemplate"),
          url: APP_ROUTES.VISIT_REQUESTED_INFO_TO_STAFF,
          routeName: ROUTE_NAMES.VISIT_REQUESTED_INFO_TO_STAFF_SEARCH,
        },
        {
          id: 12,
          title: t("visitRequestedForInformationToVisitorTemplate"),
          url: APP_ROUTES.VISIT_REQUESTED_INFO_TO_VISITOR,
          routeName: ROUTE_NAMES.VISIT_REQUESTED_INFO_TO_VISITOR_SEARCH,
        },
        {
          id: 13,
          title: t("visitorCheckInTemplate"),
          url: APP_ROUTES.VISITOR_CHECK_IN,
          routeName: ROUTE_NAMES.VISITOR_CHECK_IN_SEARCH,
        },
        {
          id: 14,
          title: t("visitorCheckOutTemplate"),
          url: APP_ROUTES.VISITOR_CHECK_OUT,
          routeName: ROUTE_NAMES.VISITOR_CHECK_OUT_SEARCH,
        },
        {
          id: 18,
          title: t("finalPermitApprovalTemplate"),
          url: APP_ROUTES.FINAL_PERMIT_APPROVAL_TEMPLATE,
          routeName: ROUTE_NAMES.FINAL_PERMIT_APPROVAL_SEARCH,
        },
      ],
      icon: <Mail className="w-5" />,
    },
    {
      id: "8",
      title: t("blacklistVisitors"),
      items: [
        {
          id: 1,
          title: t("addBlacklistVisitor"),
          url: APP_ROUTES.ADD_BLACKLIST_VISITOR,
          routeName: ROUTE_NAMES.BLACKLIST_CREATE,
        },

        {
          id: 2,
          title: t("searchBlacklistVisitors"),
          url: APP_ROUTES.SEARCH_BLACKLIST_VISITORS,
          routeName: ROUTE_NAMES.BLACKLIST_SEARCH,
        },
      ],
      icon: <Ban className="w-5" />,
    },
  ];

  const filteredSidebarItems = sidebarItems
    .map(({ routeName, items, icon, ...rest }) => {
      const hasPrivilege = userRoles?.rolePrivileges?.some(
        (privilege: RolePrivilege) => privilege.s_RouteName === routeName,
      );

      const filteredItems = items?.filter((item) =>
        userRoles?.rolePrivileges?.some(
          (privilege: RolePrivilege) =>
            privilege.s_RouteName === item.routeName,
        ),
      );

      if (hasPrivilege || filteredItems?.length) {
        return { ...rest, routeName, items: filteredItems, icon };
      }

      return null;
    })
    .filter(Boolean) as SidebarItem[];

  const handleNavigate = useCallback(
    (path?: string) => {
      if (path) {
        navigate(path);
        onClose();
      }
    },
    [navigate, onClose],
  );

  return (
    <div
      className={cn(
        "fixed dark:text-black flex flex-col gap-16 z-[999] top-0 w-screen sm:w-[50vw] md:w-[40vw] lg:w-[20vw] h-full border-2 bg-white transform py-5 px-[14px] transition-transform duration-300 ease-in-out overflow-y-auto",
        isArabic
          ? "right-0 border-l-2 translate-x-full"
          : "left-0 border-r-2 -translate-x-full",
        isOpen ? "translate-x-0" : "",
      )}
    >
      <div className="flex items-center gap-7">
        <X className="w-8 cursor-pointer" onClick={onClose} />
      </div>

      <nav>
        <ul className="flex flex-col -mt-10 md:mt-0">
          {filteredSidebarItems.map(({ id, items, icon: Icon, title, url }) => (
            <li
              key={id}
              className="cursor-pointer rounded-[5px] px-4 flex items-center justify-between"
            >
              {items ? (
                <Accordian
                  id={id}
                  itemStyle="-my-0.5"
                  title={
                    <div className="flex items-center gap-2 group">
                      <div>{Icon}</div>
                      <p
                        className={cn(
                          "font-inter font-medium group-hover:font-semibold text-[17px]",
                          i18n.language === "en" ? "text-left" : "text-right",
                        )}
                      >
                        {title}
                      </p>
                    </div>
                  }
                  selectedKey={selectedKey}
                  onSelectionChange={setSelectedKey}
                  chevronStyle="w-4 pt-2"
                >
                  <div className="flex flex-col gap-1.5 -mt-3 max-h-[40vh] overflow-y-auto pr-2">
                    {items.map((subItem) => (
                      <p
                        key={subItem.id}
                        onClick={() => handleNavigate(subItem.url)}
                        className={cn(
                          "text-sm hover:font-semibold",
                          location.pathname === subItem.url ||
                            subItem.subUrls?.includes(location.pathname)
                            ? "font-semibold"
                            : "font-light",
                          isArabic ? "mr-7" : "ml-7",
                        )}
                      >
                        {subItem.title}
                      </p>
                    ))}
                  </div>
                </Accordian>
              ) : (
                <div
                  onClick={() => handleNavigate(url)}
                  className="flex items-center gap-2 ml-2 pb-4 group w-full"
                >
                  {Icon}
                  <p
                    className={cn(
                      "font-inter group-hover:font-semibold text-[17px]",
                      location.pathname === url
                        ? "font-semibold"
                        : "font-medium",
                    )}
                  >
                    {title}
                  </p>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default React.memo(Sidebar);
